/**
 * Referenced MUI component LinearProgress
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { COLOR } from '../../styles';
import { type ProgressBarProps, type StylesType } from './ProgressBar.types';

const ProgressBar: React.FC<ProgressBarProps> = (props) => {
  const { className = '', style, ...wrapperProps } = props;
  wrapperProps.mode = wrapperProps.mode ?? 'indeterminate';
  wrapperProps.value = wrapperProps.value ?? 0;
  wrapperProps.min = wrapperProps.min ?? 0;
  wrapperProps.max = wrapperProps.max ?? 100;
  const { color, max, min, mode, transitionDuration, value } = wrapperProps;
  const bar1Ref = useRef(null);
  const bar2Ref = useRef(null);
  const [timers, setTimers] = useState({ bar1: null, bar2: null });

  // NOTE: This method mutates `step` and `barElement`
  // eslint-disable-next-line
  // @ts-ignore
  const barUpdate = useCallback(
    // eslint-disable-next-line
    // @ts-ignore
    (id, step = 0, barElement, stepValues) => {
      if (mode !== 'indeterminate' || !barElement) return;

      step %= 4;

      const right = 'right'; // left and right need to be flipped if have RTL theme in the future
      const left = 'left';

      if (step === 0) {
        barElement.style[left] = stepValues[0][0] + '%';
        barElement.style[right] = stepValues[0][1] + '%';
      } else if (step === 1) {
        barElement.style.transitionDuration = '840ms';
      } else if (step === 2) {
        barElement.style[left] = stepValues[1][0] + '%';
        barElement.style[right] = stepValues[1][1] + '%';
      } else if (step === 3) {
        barElement.style.transitionDuration = '0ms';
      }

      // eslint-disable-next-line
      // @ts-ignore
      timers[id] = setTimeout(() => barUpdate(id, step + 1, barElement, stepValues), 420);
    },
    [mode, timers],
  );

  useEffect(() => {
    const bar1 = barUpdate('bar1', 0, bar1Ref?.current, [
      [-35, 100],
      [100, -90],
    ]);

    const bar2 = setTimeout(() => {
      barUpdate('bar2', 0, bar2Ref?.current, [
        [-200, 100],
        [107, -8],
      ]);
    }, 850);

    // eslint-disable-next-line
    // @ts-ignore
    setTimers({ bar1, bar2 });

    return () => {
      // eslint-disable-next-line
      // @ts-ignore
      clearTimeout(timers.bar1);
      // eslint-disable-next-line
      // @ts-ignore
      clearTimeout(timers.bar2);
    };
  }, []);

  // eslint-disable-next-line
  // @ts-ignore
  const getRelativeValue = (value, min, max) => {
    const clampedValue = Math.min(Math.max(min, value), max);
    const rangeValue = max - min;
    const relValue = Math.round(((clampedValue - min) / rangeValue) * 10000) / 10000;
    return relValue * 100;
  };

  const getStyles = () => {
    const styles: StylesType = {
      bar: {},
      barFragment1: {},
      barFragment2: {},
    };

    if (mode === 'indeterminate') {
      styles.barFragment1 = {
        position: 'absolute',
        backgroundColor: color || '#00bcd4',
        top: 0,
        left: 0,
        bottom: 0,
        transition: 'all 840ms cubic-bezier(0.650, 0.815, 0.735, 0.395)',
      };

      styles.barFragment2 = {
        position: 'absolute',
        backgroundColor: color || '#00bcd4',
        top: 0,
        left: 0,
        bottom: 0,
        transition: 'all 840ms cubic-bezier(0.165, 0.840, 0.440, 1.000)',
      };
    } else {
      styles.bar.backgroundColor = color || '#00bcd4';
      styles.bar.transition = `width ${transitionDuration || '.3s'} linear`;
      styles.bar.width = getRelativeValue(value, min, max) + '%';
    }

    return styles;
  };

  const styles = getStyles();

  return (
    <Wrapper
      data-testid="progress-bar-wrapper"
      className={`progress-bar-wrapper ${className}`}
      style={{ ...style }}
      {...wrapperProps}
    >
      <StyledProgressBar className="progress-bar" style={styles.bar}>
        <div ref={bar1Ref} style={styles.barFragment1} />
        <div ref={bar2Ref} style={styles.barFragment2} />
      </StyledProgressBar>
    </Wrapper>
  );
};
// Styled Components
const Wrapper = styled.div`
  position: relative;
  height: 4px;
  display: block;
  width: 100%;
  background-color: ${COLOR['light-gray-dark']};
  border-radius: 2px;
  margin: 0;
  overflow: hidden;
`;

const StyledProgressBar = styled.div`
  height: 100%;
`;

// Display Name
ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
